import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { Modal } from "@/components/Modal";
import { requestIdForIntent, type IntentRequestIdentity } from "@/lib/request-identity";
import {
  applyProviderDefaults,
  isAttachmentImageMediaType,
  providerStatusesForWorker,
  RivetsApiError,
  type AttachmentRef,
  type CreateTaskResponse,
  type GitHubRepoOption,
  type ProviderSelection,
  type ReasoningEffort,
  type Repo,
  type WorkerInfo,
} from "@rivets/core";
import { taskCreationBlocker } from "./taskCreationAvailability";
import { TaskStartPicker } from "./TaskStartPicker";
import { taskModelSeed } from "./taskModel";
import { taskStartAllowsEmptyPrompt, taskStartRequest, type TaskStartSelection } from "./taskStart";
import { useAgentDefaults } from "@/hooks/useAgentDefaults";
import { isSubmitKeyPress, submitShortcutLabel, useSubmitShortcut } from "@/lib/submitShortcut";
import { useInitialFocus } from "@/hooks/useInitialFocus";
import { useAttachments } from "@/features/chat/useAttachments";
import { AttachButton, AttachmentTray } from "@/features/chat/AttachmentTray";
import { AgentModelPicker, EffortPicker, RuntimeToggle } from "@/features/chat/RuntimeControls";
import { MenuSelect, type MenuSelectOption } from "@/components/MenuSelect";
import { RepoIdentity } from "@/components/RepoIdentity";
import { useShortcutCommand } from "@/lib/keyboardShortcuts";
import { useScrollEdges } from "@/lib/scrollEdges";
import { useOrgContext } from "@/lib/OrgContext";
import { usableWorkerClusters } from "@/lib/workerClusters";
import { apiErrorText, usableWorkers } from "@/features/workers/worker-access";
import { resizeTaskComposer } from "@/lib/taskComposer";
import { isOutcomeUncertain } from "@/lib/outcome";
import { newTaskDraftBelongsHere, useNewTaskDraft } from "./newTaskDraftStore";

const NO_WORKERS: WorkerInfo[] = [];

export function NewTaskModal({
  open,
  onClose,
  orgId,
  repos,
  defaultRepoId,
  initialKind = "task",
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  repos: Repo[];
  defaultRepoId?: string;
  initialKind?: "task" | "repo";
  onCreated: (result: CreateTaskResponse) => void;
}) {
  const { role } = useOrgContext();
  const [repoId, setRepoId] = useState(defaultRepoId ?? repos[0]?.id ?? "");
  const [prompt, setPrompt] = useState("");
  const [start, setStart] = useState<TaskStartSelection>();
  const [clusterId, setClusterId] = useState("auto");
  const [providerId, setProviderId] = useState("");
  const [modelId, setModelId] = useState("");
  const [effort, setEffort] = useState<ReasoningEffort | "">("");
  const [fastMode, setFastMode] = useState(false);
  const [planMode, setPlanMode] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [kind, setKind] = useState<"task" | "repo">("task");
  const requestIdentity = useRef<IntentRequestIdentity | undefined>(undefined);
  const submittedAttachments = useRef<AttachmentRef[] | undefined>(undefined);
  const submitting = useRef(false);
  const [submittedTitle, setSubmittedTitle] = useState("New task");
  // Keyed on the mode too: a card last left on the Repository tab opens on it
  // for one render before the reset below puts it back on Task, and the
  // prompt is not on the page to take the caret until then.
  const promptRef = useInitialFocus<HTMLTextAreaElement>(open && kind === "task");
  useShortcutCommand("focusPromptComposer", () => {
    promptRef.current?.focus();
  }, { enabled: open && kind === "task", priority: 20 });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (!open) return;
    // The prompt left here last time — closed, reloaded, or a create that
    // failed — comes back (CONTRACT C3); it goes only once a task is made.
    // Opened for one project, the card is that project's: a prompt left for
    // another one waits for the global "New task" instead.
    const saved = useNewTaskDraft.getState().draft;
    const draft = saved && newTaskDraftBelongsHere(saved, defaultRepoId) ? saved : null;
    const draftRepo = draft?.repoId && repos.some((repo) => repo.id === draft.repoId) ? draft.repoId : undefined;
    const nextRepoId = defaultRepoId ?? draftRepo ?? repos[0]?.id ?? "";
    setRepoId(nextRepoId);
    setPrompt(draft?.text ?? "");
    setStart(undefined);
    setClusterId("auto");
    setProviderId("");
    setModelId("");
    setEffort("");
    setFastMode(false);
    setPlanMode(false);
    setKind(role === "owner" ? initialKind : "task");
    submitting.current = false;
    requestIdentity.current = undefined;
    // Not keyed on `repos`: connecting a repository from the Repository tab
    // changes the list, and that must not throw the card back to its opening
    // state. The effect below keeps the selection valid as the list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultRepoId, initialKind, role]);

  // Repositories can arrive after the card opens, and one can leave while it
  // is up; either way the picker falls back rather than pointing at nothing.
  useEffect(() => {
    if (!open || repos.some((repo) => repo.id === repoId)) return;
    const fallback = defaultRepoId && repos.some((repo) => repo.id === defaultRepoId) ? defaultRepoId : repos[0]?.id;
    if (fallback) setRepoId(fallback);
  }, [open, repos, repoId, defaultRepoId]);

  const attachments = useAttachments({ orgId, disabled: !open || kind !== "task" });

  useEffect(() => {
    if (open) attachments.clear();
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    resizeTaskComposer(promptRef.current);
  }, [open, prompt]);

  const workersQuery = useQuery({
    queryKey: qk.workers(orgId),
    queryFn: () => client.listWorkers(orgId),
    enabled: open && repos.length > 0,
  });
  const workers = workersQuery.data?.workers ?? NO_WORKERS;
  // Only machines this member may run work on are offered, or counted: the
  // server refuses a pin to one they may not use, and Auto never picks one.
  const usable = useMemo(() => usableWorkers(workers), [workers]);
  const selectedRepo = repos.find((repo) => repo.id === repoId);
  const clusters = useMemo(() => usableWorkerClusters(workers), [workers]);
  const routingWorkers = useMemo(() => {
    const compatible = usable.filter((worker) =>
      worker.status === "online" && workerCanRunRepo(worker, selectedRepo));
    return clusterId === "auto" ? compatible : compatible.filter((worker) => worker.clusterId === clusterId);
  }, [clusterId, selectedRepo?.auth, usable]);
  const selectedCluster = clusterId === "auto" ? undefined : clusters.find((cluster) => cluster.id === clusterId);
  // A pinned cluster that stops being shared mid-compose (its last usable
  // worker's grant was withdrawn) goes back to Auto rather than pinning a
  // route the server will refuse.
  useEffect(() => {
    if (clusterId !== "auto" && workersQuery.data && !selectedCluster) setClusterId("auto");
  }, [clusterId, selectedCluster, workersQuery.data]);
  const providers = useMemo(() => {
    const ready = new Map<string, ReturnType<typeof providerStatusesForWorker>[number]>();
    for (const worker of routingWorkers) {
      for (const status of providerStatusesForWorker(worker.capabilities, true)) {
        if (status.readiness === "ready" && !ready.has(status.providerId)) ready.set(status.providerId, status);
      }
    }
    return [...ready.values()];
  }, [routingWorkers]);
  const creationBlocker = clusterId !== "auto" && selectedCluster && routingWorkers.length === 0
    ? {
        reason: "workers" as const,
        title: "Selected cluster unavailable",
        message: !selectedCluster.workers.some((worker) => worker.status === "online")
          ? `${selectedCluster.name} is offline. Choose Auto or another online cluster.`
          : `${selectedCluster.name} cannot access this repository. Choose a compatible cluster.`,
      }
    : taskCreationBlocker({
        repositoryCount: repos.length,
        checkingWorkers: workersQuery.isPending,
        workerCheckFailed: workersQuery.isError,
        registeredWorkerCount: workers.length,
        usableWorkerCount: usable.length,
        onlineWorkerCount: routingWorkers.length,
        readyProviderCount: providers.length,
      });

  // The account's saved defaults, narrowed to what this route can actually run.
  // They only ever decide the preselection; every field stays switchable, and a
  // default the routed workers do not offer falls back to the previous
  // worker-derived choice rather than blocking creation.
  const accountDefaults = useAgentDefaults();
  const submitShortcut = useSubmitShortcut();
  const preferred = useMemo(() => applyProviderDefaults(accountDefaults, providers), [accountDefaults, providers]);
  /** Model and effort defaults only apply to the agent they were saved for. */
  const preferredApplies = preferred.providerId === providerId;

  useEffect(() => {
    if (providers.some((provider) => provider.providerId === providerId)) return;
    setProviderId(preferred.providerId ?? providers[0]?.providerId ?? "");
  }, [preferred.providerId, providerId, providers]);

  // A branch or pull request belongs to one repository; an issue is not tied
  // to one, but switching repositories is the moment to rethink it either way.
  useEffect(() => { setStart(undefined); }, [repoId]);
  const promptReady = Boolean(prompt.trim()) || taskStartAllowsEmptyPrompt(start);

  const selectedProvider = providers.find((provider) => provider.providerId === providerId);
  // A model is only offerable when the worker reports the capability; the CLI
  // already clears `modelSelection` for providers that expose no models.
  const models = useMemo(
    () => (selectedProvider?.features.modelSelection ? selectedProvider.models : []),
    [selectedProvider],
  );
  const defaultModel = selectedProvider?.models.find((model) => model.isDefault);
  const selectedModel = models.find((model) => model.id === modelId) ?? defaultModel;
  const efforts = selectedModel?.reasoningEfforts ?? selectedProvider?.reasoningEfforts ?? [];

  useEffect(() => {
    if (models.some((model) => model.id === modelId)) return;
    // An empty id names no model; the picker, efforts and fast mode then fall
    // back to `defaultModel`, the model such a task runs on.
    setModelId(taskModelSeed(selectedProvider, preferredApplies ? preferred.model : undefined));
  }, [modelId, models, preferred.model, preferredApplies, selectedProvider]);

  useEffect(() => {
    if (effort && efforts.includes(effort)) return;
    setEffort((preferredApplies ? preferred.reasoningEffort : undefined) ?? efforts[0] ?? "");
  }, [effort, efforts, preferred.reasoningEffort, preferredApplies]);

  const provider: ProviderSelection | undefined = selectedProvider
    ? {
        providerId: selectedProvider.providerId,
        model: selectedProvider.features.modelSelection ? selectedModel?.id : undefined,
        reasoningEffort: effort || efforts[0],
        fastMode: selectedProvider.features.fastMode && selectedModel?.supportsFastMode ? fastMode : undefined,
        planMode: selectedProvider.features.planMode ? planMode : undefined,
      }
    : undefined;

  const hasNativeImages = attachments.items.some((item) => isAttachmentImageMediaType(item.mediaType));
  const attachmentsUnsupported = selectedProvider !== undefined
    && !selectedProvider.features.imageInput
    && hasNativeImages;

  function closeModal() {
    attachments.clear();
    onClose();
  }

  const createMutation = useMutation({
    mutationFn: ({ attachmentRefs }: { attachmentRefs?: AttachmentRef[] }) => {
      if (!provider) throw new Error("Choose an available agent.");
      const routing = clusterId === "auto"
        ? { mode: "auto" as const }
        : { mode: "cluster" as const, clusterId };
      const startRequest = taskStartRequest(start);
      const intent = JSON.stringify({ repoId, prompt: prompt.trim(), start: startRequest, provider, routing, attachments: attachmentRefs?.map((ref) => ref.id) ?? [] });
      requestIdentity.current = requestIdForIntent(requestIdentity.current, intent);
      return client.createTask(repoId, {
        provider,
        prompt: prompt.trim(),
        start: startRequest,
        routing,
        attachments: attachmentRefs,
        clientRequestId: requestIdentity.current.id,
      });
    },
    onError: (error) => {
      // It may exist (CONTRACT P7): the lists are read back, and a task made
      // after all appears in them. The prompt stays as the draft either way.
      if (isOutcomeUncertain(error)) void queryClient.invalidateQueries({ queryKey: ["worktrees"] });
    },
    onSuccess: (result) => {
      requestIdentity.current = undefined;
      useNewTaskDraft.getState().clear();
      onCreated(result);
      closeModal();
    },
    onSettled: () => { submitting.current = false; },
  });

  function selectAgentModel(value: string) {
    const [nextProviderId, nextModelId] = JSON.parse(value) as [string, string];
    setProviderId(nextProviderId);
    setModelId(nextModelId);
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (isSubmitKeyPress(event, submitShortcut)) {
      event.preventDefault();
      void submitTask();
    }
  }

  async function submitTask() {
    if (submitting.current || !promptReady || !provider || creationBlocker || attachments.failed || attachmentsUnsupported) return;
    submitting.current = true;
    setSubmittedTitle(prompt.trim().split(/\r?\n/, 1)[0]?.slice(0, 100)
      || (start?.kind === "linearIssue" ? start.issue.title : "New task"));
    let refs = attachments.refs;
    if (attachments.uploading) {
      setFlushing(true);
      try {
        refs = await attachments.settle();
      } finally {
        setFlushing(false);
      }
    }
    if (refs.length < attachments.items.length) {
      submitting.current = false;
      return;
    }
    submittedAttachments.current = refs.length ? refs : undefined;
    closeModal();
    createMutation.mutate({ attachmentRefs: submittedAttachments.current });
  }

  const backgroundCreation = !open && (createMutation.isPending || createMutation.isError);

  const clusterOptions: MenuSelectOption[] = [
    { value: "auto", label: "Auto", detail: "Best available worker" },
    ...clusters.map((cluster) => {
      const compatible = cluster.workers.filter((worker) => worker.status === "online" && workerCanRunRepo(worker, selectedRepo));
      const online = cluster.workers.some((worker) => worker.status === "online");
      const capacity = compatible.reduce((sum, worker) => sum + (worker.capabilities?.maxConcurrentJobs ?? 0), 0);
      return {
        value: cluster.id,
        label: cluster.name,
        meta: !online ? "Offline" : compatible.length === 0 ? "No access" : `${compatible.length} · ${capacity} slots`,
        disabled: compatible.length === 0,
      };
    }),
  ];

  // Only genuine faults get a line of their own, and only while one is live.
  // The routing explainer that used to hold this slot open said the same thing
  // on every visit and cost the card two permanent lines; it is now the cluster
  // control's own tooltip, read on demand by the one person in ten who wonders.
  const fault = attachmentsUnsupported
    ? `${selectedProvider?.displayName ?? "This agent"} does not accept images — remove them or pick another agent.`
    : attachments.failed
      ? "A file failed to upload. Retry or remove it to create the task."
      : createMutation.isError
        ? apiErrorText(createMutation.error, "Could not create task.")
        : undefined;

  const clusterHint = clusterId === "auto"
    ? "Worker cluster — Auto picks the nearest compatible online worker, then balances its load."
    : "Worker cluster — pinned, so this task will not route outside this cluster's worker pool.";

  const submitBlocked = createMutation.isPending || flushing || workersQuery.isPending
    || Boolean(creationBlocker) || !promptReady || !provider
    || attachments.failed || attachmentsUnsupported;

  return (
    <>
    <Modal open={open} onClose={closeModal} title={kind === "task" ? "New task" : "Add GitHub repository"} width={720} hideHeader>
      <div className="task-modal-header">
        <div className="task-modal-scope">
          <MenuSelect
            ariaLabel="Repository"
            value={repoId}
            onChange={(next: string) => {
              setRepoId(next);
              // An empty card holds no draft of its own; moving one would
              // carry off the prompt it declined to restore.
              if (prompt.trim()) useNewTaskDraft.getState().setRepo(next);
            }}
            options={repos.map((repo) => ({ value: repo.id, label: repo.name, detail: repo.githubFullName ?? undefined }))}
            leading={selectedRepo ? <RepoIdentity repo={selectedRepo} size={18} /> : undefined}
            placeholder="Choose repository"
            searchable
            searchPlaceholder="Search repositories…"
            minWidth={168}
            maxWidth={264}
          />
          <TaskStartPicker orgId={orgId} repo={selectedRepo} value={start} onChange={setStart} />
        </div>
        {/* Two quiet text tabs, no track. Each reserves its own bold width via
            `data-label`, so the switch in weight on selection cannot nudge the
            other one sideways. */}
        <div className="quiet-tabs" role="tablist" aria-label="Create new">
          {(role === "owner" ? ["task", "repo"] as const : ["task"] as const).map((option) => {
            const label = option === "task" ? "Task" : "Repository";
            return (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={kind === option}
                data-label={label}
                className={`quiet-tab${kind === option ? " active" : ""}`}
                onClick={() => setKind(option)}
              ><span>{label}</span></button>
            );
          })}
        </div>
      </div>
      {kind === "repo" && role === "owner" ? <AddGitHubRepository orgId={orgId} repos={repos} onAdded={(repo) => { setRepoId(repo.id); setKind("task"); }} /> : (
      <form
        className="task-modal-form"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (submitting.current) return;
          void submitTask();
        }}
      >
        {creationBlocker ? (
          <div className="task-creation-alert" role="alert">
            <svg className="task-creation-alert-icon" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M10 2.5 18 17H2L10 2.5Z" />
              <path d="M10 7v4.5M10 14.5h.01" />
            </svg>
            <div className="task-creation-alert-copy">
              <strong>{creationBlocker.title}</strong>
              <span>{role !== "owner" && creationBlocker.reason === "repositories"
                ? "Ask an organization owner to add you to a team with repository access."
                : creationBlocker.message}</span>
              {creationBlocker.reason === "worker-check" ? (
                <button
                  className="task-creation-alert-retry"
                  type="button"
                  onClick={() => workersQuery.refetch()}
                >
                  Try again
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className={`task-prompt${attachments.dragging ? " dropping" : ""}`} {...attachments.dropZoneProps}>
          <AttachmentTray attachments={attachments} />
          <textarea
            id="task-prompt"
            ref={promptRef}
            className="task-prompt-input"
            required={!taskStartAllowsEmptyPrompt(start)}
            rows={5}
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
              useNewTaskDraft.getState().setText(event.target.value, repoId);
            }}
            onKeyDown={(event) => handleComposerKeyDown(event)}
            onPaste={attachments.handlePaste}
            placeholder={start?.kind === "linearIssue"
              ? `Add instructions for ${start.issue.identifier}, or create to work on the issue as written…`
              : "Describe what you want the agent to do…"}
          />
          {attachments.dragging ? <div className="composer-drop-hint" aria-hidden="true">Drop files to attach</div> : null}
        </div>

        {/* No reserved slot: a fault is rare, belongs to the prompt it blocks,
            and pushes the footer down only for as long as it is true. */}
        {fault ? <p className="task-modal-fault" role="alert">{fault}</p> : null}

        <div className="task-modal-footer">
          <div className="task-modal-runtime">
            <div className="runtime-menus">
              <AgentModelPicker
                providerId={providerId}
                modelId={selectedModel?.id}
                statuses={providers}
                onChange={selectAgentModel}
              />
              {selectedProvider?.features.reasoningEffort && efforts.length > 0 ? (
                <EffortPicker value={effort || efforts[0]} efforts={efforts} onChange={setEffort} />
              ) : null}
              <MenuSelect
                className="task-cluster-picker"
                ariaLabel="Worker cluster"
                title={clusterHint}
                value={clusterId}
                onChange={setClusterId}
                options={clusterOptions}
                disabled={workersQuery.isPending || workersQuery.isError || usable.length === 0}
                minWidth={90}
                maxWidth={90}
                size="sm"
              />
            </div>
            <span className="runtime-separator" aria-hidden="true" />
            <div className="runtime-toggles">
              {selectedProvider?.features.fastMode && selectedModel?.supportsFastMode ? (
                <RuntimeToggle kind="fast" active={fastMode} onToggle={() => setFastMode((value) => !value)} />
              ) : null}
              {selectedProvider?.features.planMode ? (
                <RuntimeToggle kind="plan" active={planMode} onToggle={() => setPlanMode((value) => !value)} />
              ) : null}
              <AttachButton attachments={attachments} disabled={createMutation.isPending || flushing} />
            </div>
          </div>
          <div className="task-modal-actions">
            <button className="btn btn-ghost" type="button" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" type="submit" title={`Create task (${submitShortcutLabel(submitShortcut)})`} disabled={submitBlocked}>
              {createMutation.isPending || flushing ? "Creating…" : <>Create<span className="btn-kbd" aria-hidden="true">↵</span></>}
            </button>
          </div>
        </div>
      </form>
      )}
    </Modal>
    {backgroundCreation ? createPortal(
      <div className="task-creation-progress" role={createMutation.isError ? "alertdialog" : "status"} aria-live="polite">
        <div className="task-creation-progress-content">
          {createMutation.isPending ? <div className="spinner" aria-hidden="true" /> : null}
          <strong>{createMutation.isPending ? "Creating task" : isOutcomeUncertain(createMutation.error) ? "Checking on the task" : "Could not create task"}</strong>
          <span>
            {createMutation.isPending
              ? `Setting up ${submittedTitle}…`
              : isOutcomeUncertain(createMutation.error)
                ? "Couldn't confirm the task was created — checking…"
              : createMutation.error instanceof RivetsApiError
                ? createMutation.error.message
                : createMutation.error?.message || "Could not create task."}
          </span>
          {createMutation.isError ? (
            <div className="task-creation-progress-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => { submitting.current = true; createMutation.mutate({ attachmentRefs: submittedAttachments.current }); }}
              >
                Try again
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  submitting.current = false;
                  requestIdentity.current = undefined;
                  createMutation.reset();
                }}
              >
                Dismiss
              </button>
            </div>
          ) : null}
        </div>
      </div>,
      document.body,
    ) : null}
    </>
  );
}

/** Hands back the connected repository, which the task form then selects:
 * a repository connected from inside the card is the one the task is for. */
function AddGitHubRepository({ orgId, repos, onAdded }: { orgId: string; repos: Repo[]; onAdded: (repo: Repo) => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<GitHubRepoOption>();
  const githubQuery = useQuery({
    queryKey: qk.github(orgId),
    queryFn: () => client.githubStatus(orgId),
    retry: (count, error) => !(error instanceof RivetsApiError && error.code === "github_not_configured") && count < 2,
  });
  const connected = githubQuery.data?.installation?.status === "active";
  const optionsQuery = useQuery({ queryKey: qk.githubRepos(orgId), queryFn: () => client.githubRepos(orgId), enabled: connected });
  const connectedIds = new Set(repos.map((repo) => repo.githubRepoId).filter((id): id is number => id != null));
  const visible = (optionsQuery.data?.repos ?? []).filter((repo) =>
    !connectedIds.has(repo.githubRepoId) && repo.fullName.toLowerCase().includes(search.trim().toLowerCase()),
  );
  // The fade is a fact about the list, not a decoration: it appears only while
  // there is something past that edge. This used to be hand-rolled React state
  // for the bottom edge alone; `useScrollEdges` measures both and re-renders
  // nothing.
  const listRef = useScrollEdges("y");

  const addMutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error("Choose a repository.");
      return client.createRepo(orgId, {
        githubRepoId: selected.githubRepoId,
        name: selected.fullName.split("/").at(-1) ?? selected.fullName,
        defaultBranch: selected.defaultBranch,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData<{ repos: Repo[] }>(qk.repos(orgId), (old) =>
        old ? { repos: [result.repo, ...old.repos.filter((repo) => repo.id !== result.repo.id)] } : { repos: [result.repo] },
      );
      onAdded(result.repo);
    },
  });

  if (githubQuery.error instanceof RivetsApiError && githubQuery.error.code === "github_not_configured") {
    return <p className="modal-intro">GitHub repository connections are not configured for this deployment.</p>;
  }
  if (githubQuery.data && !githubQuery.data.installation) {
    return <div className="new-repo-empty"><p className="modal-intro">Connect the Rivets GitHub App to choose a repository.</p><a className="btn btn-primary btn-compact" href={githubQuery.data.installUrl}>Connect GitHub</a></div>;
  }
  // Until both the connection and the listing have answered, the picker is
  // drawn at its full size with a spinner where the rows will be — the card
  // holds the shape it is about to have instead of growing when they land.
  const loading = githubQuery.isPending || (connected && optionsQuery.isPending);

  return (
    <div className="new-repo-picker">
      {/* No title and no explainer: the tab above already says "Repository",
          the search field's placeholder says what to type, and the sentence
          that used to sit here told a returning user nothing they did not
          learn the first time. */}
      <input className="input" autoFocus aria-label="Search repositories" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search repositories…" />
      {/* The list is as tall as its contents up to a ceiling, and says so with a
          fade when there is more below the fold. */}
      <div className="new-repo-list-wrap">
        <div className={`new-repo-list${loading ? " loading" : ""}`} role="listbox" aria-label="GitHub repositories" aria-busy={loading} ref={listRef}>
          {loading ? <span className="spinner" role="status" aria-label="Loading repositories" /> : null}
          {!loading && visible.length === 0 ? <div className="hint">No repositories available.</div> : null}
          {visible.map((repo) => (
            <button type="button" role="option" aria-selected={selected?.githubRepoId === repo.githubRepoId} className={`new-repo-row${selected?.githubRepoId === repo.githubRepoId ? " selected" : ""}`} key={repo.githubRepoId} onClick={() => setSelected(repo)}>
              {/* Neutral chip: a repository this org has not given an icon to is
                  not entitled to a colour of its own. */}
              <span className="new-repo-mark" aria-hidden="true">{repo.fullName.split("/").at(-1)?.charAt(0).toUpperCase() || "R"}</span>
              <span className="new-repo-name truncate">{repo.fullName}</span>
              {/* Always rendered, fixed width: the column holds its place on a
                  row that happens to have nothing to put in it. */}
              <span className="new-repo-meta mono truncate">{repo.private ? "Private" : "Public"} · {repo.defaultBranch}</span>
            </button>
          ))}
        </div>
      </div>
      {addMutation.isError ? <div className="error-text">{addMutation.error instanceof RivetsApiError ? addMutation.error.message : "Could not add repository."}</div> : null}
      <div className="modal-footer"><button className="btn btn-primary" type="button" disabled={!selected || addMutation.isPending} onClick={() => addMutation.mutate()}>{addMutation.isPending ? "Adding…" : "Add repository"}</button></div>
    </div>
  );
}

function workerCanRunRepo(worker: WorkerInfo, repo: Repo | undefined): boolean {
  return repo?.auth !== "github-app" || worker.capabilities?.gitCredentialHelper === true;
}

